// Bundler-safe default WASM loader shared by the 5 per-dialect tree-sitter
// parser singletons (mssql/mysql/oracle/postgres/sqlite).
//
// Two runtimes reach this code:
//   • Electron renderer (browser context, no `node:fs`): the parser file is
//     bundled by Vite into `dist/assets`, so a relative `import.meta.url`
//     path no longer points at the repo's `resources/`. The renderer must
//     resolve the grammar through Vite's `?url` asset pipeline (which copies
//     the wasm into the build and rewrites the import to the served/packaged
//     URL) and `fetch` the bytes. This is the path the WASM-bundling E2E
//     contract describes.
//   • Node / Vitest / Electron main: `window` is undefined and `node:fs` is
//     available, so we read the wasm straight off disk. The layout differs
//     between running from SOURCE (vitest, dev) and running from the Vite main
//     bundle (`dist-electron/*.cjs`, and the same bundle inside `app.asar`), so
//     several candidate paths are tried in order and the first that exists
//     wins — a single `import.meta.url`-relative path silently resolved
//     outside the packaged app. Unit tests may still inject their own loader
//     via the per-engine `setXxxWasmLoader` seam.

/** Loader that returns the bytes of a `?url`-imported asset's URL. Each engine
 *  passes a thunk that dynamic-imports its own `<engine>.wasm?url` so Vite can
 *  statically discover and copy the asset. Kept lazy so the `?url` import never
 *  enters the node/vitest static graph — only the renderer branch evaluates it. */
export type WasmUrlLoader = () => Promise<{ default: string }>;

/**
 * Resolve the grammar wasm bytes for one engine in a bundler-safe way.
 *
 * @param engine     dialect name, used only to derive the node disk path.
 * @param importUrl  `import.meta.url` of the calling parser module (node path).
 * @param loadUrl    thunk dynamic-importing `<engine>.wasm?url` (renderer path).
 */
export async function loadGrammarWasmBytes(
  engine: 'mssql' | 'mysql' | 'oracle' | 'postgres' | 'sqlite',
  importUrl: string,
  loadUrl: WasmUrlLoader,
): Promise<Uint8Array> {
  // Renderer: Vite-bundled `?url` asset + fetch. `window` is the reliable
  // browser/renderer signal; vitest + Electron main run without it.
  if (typeof window !== 'undefined' && typeof fetch === 'function') {
    const { default: wasmUrl } = await loadUrl();
    const res = await fetch(wasmUrl);
    if (!res.ok) {
      throw new Error(
        `normalize/${engine}: failed to fetch grammar wasm (${res.status} ${res.statusText}) from ${wasmUrl}`,
      );
    }
    return new Uint8Array(await res.arrayBuffer());
  }

  // Node / Vitest / Electron main: read the wasm off disk. WHERE it sits
  // depends on how this module was loaded — from SOURCE it is five directories
  // up (`src/features/sql-grammar/normalize/<engine>/` → repo root), from the
  // Vite main bundle it is one to three up (`dist-electron/**/*.cjs` →
  // `dist-electron/..`), and inside `app.asar` the same bundle layout applies
  // (`readFileSync` reads through asar). Rather than pin one depth, walk UP
  // from this module looking for `resources/tree-sitter/<engine>.wasm`, then
  // fall back to Electron's app path and finally the working directory. The
  // first candidate that EXISTS wins; if none does, the error names them all.
  const { fileURLToPath } = await import('node:url');
  const path = await import('node:path');
  return readGrammarWasmFromDisk(engine, path.dirname(fileURLToPath(importUrl)));
}

/**
 * Node-side half of the loader, shared with the CREATE TABLE parse
 * (`sql-grammar/ddl/grammarLoader.ts`), which anchors on `__dirname` instead of
 * `import.meta.url`: the Playwright worker requires our TypeScript as
 * CommonJS, and a CommonJS file that merely SPELLS `import.meta` is re-parsed
 * by Node 22 as an ES module and dies with "exports is not defined".
 *
 * @param engine     dialect name, names the asset.
 * @param anchorDir  directory to walk up from (the calling module's own).
 */
export async function readGrammarWasmFromDisk(
  engine: 'mssql' | 'mysql' | 'oracle' | 'postgres' | 'sqlite',
  anchorDir: string,
): Promise<Uint8Array> {
  const { readFileSync, existsSync } = await import('node:fs');
  const path = await import('node:path');
  const asset = path.join('resources', 'tree-sitter', `${engine}.wasm`);
  const ladder = Array.from({ length: MAX_PARENT_HOPS + 1 }, (_, hops) =>
    path.resolve(anchorDir, ...Array<string>(hops).fill('..'), asset),
  );
  const candidates = [
    ...ladder,
    ...(await electronAppPath()).map((root) => path.resolve(root, asset)),
    path.resolve(process.cwd(), asset),
  ];
  const found = candidates.find((candidate) => existsSync(candidate));
  if (!found) {
    throw new Error(
      `normalize/${engine}: grammar wasm not found. Tried: ${candidates.join(', ')}`,
    );
  }
  return new Uint8Array(readFileSync(found));
}

/** How far up from this module the `resources/` directory can sit: five hops
 *  from the source tree, one to three from the Vite main bundle. */
const MAX_PARENT_HOPS = 6;

/** Electron's packaged app root, or nothing when this is not an Electron main
 *  process. `electron` is imported DYNAMICALLY and only behind the runtime
 *  guard so bundlers never pull it into a renderer or a plain-node build. */
async function electronAppPath(): Promise<string[]> {
  if (!process.versions.electron) return [];
  try {
    const electron = (await import('electron')) as unknown as {
      app?: { getAppPath?: () => string };
    };
    const appPath = electron.app?.getAppPath?.();
    return appPath ? [appPath] : [];
  } catch {
    // Not reachable from this process (utility/renderer): fall through to the
    // remaining candidates rather than failing the load.
    return [];
  }
}
